import axios, { type AxiosError } from 'axios'

// axios 0.21 の isAxiosError は `typeof payload === 'object'` だけで判定し、null を渡すと
// 中で `null.isAxiosError` を読んで投げる (upstream は 0.25.0 で utils.isObject に修正)。
// TanStack の `query.error` / `query.state.error` は正常時 null なので、この層で弾く。
const isAxiosError = (error: unknown): error is AxiosError =>
  error !== null && axios.isAxiosError(error)

export const getErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    // APIからのエラーメッセージがある場合
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    // HTTPステータスに基づくデフォルトメッセージ
    switch (error.response?.status) {
      case 400:
        return '入力内容に問題があります'
      case 401:
        return '認証が必要です'
      case 403:
        return 'アクセス権限がありません'
      case 404:
        return 'リソースが見つかりません'
      case 500:
        return 'サーバーエラーが発生しました'
      default:
        return error.message || '予期しないエラーが発生しました'
    }
  }

  if (error instanceof Error) {
    return error.message
  }

  return '予期しないエラーが発生しました'
}

export const isAuthError = (error: unknown): boolean => {
  return isAxiosError(error) && error.response?.status === 401
}

/**
 * 対象が存在しない (404). 「消えた」を終端として扱う側が使う。
 * 破棄でフリー録音の event が削除されるようになったため (ADR-0039)、
 * polling は 404 で retry を止め、詳細画面は一覧へ戻る。
 */
export const isNotFoundError = (error: unknown): boolean => {
  return isAxiosError(error) && error.response?.status === 404
}

/**
 * エラーをキャプチャしてログ記録・エラートラッキングサービスに送信
 * @param error - キャプチャするエラー
 * @param context - エラーが発生したコンテキスト情報
 */
export const captureException = (
  error: unknown,
  context?: {
    context: string
    [key: string]: unknown
  }
): void => {
  // エラー情報をキャプチャ（コンソール出力は削除）
  // context情報は保持してSentryに送信

  // Sentryが利用可能な場合はエラーを送信
  if (
    typeof window !== 'undefined' &&
    'Sentry' in window &&
    typeof window.Sentry === 'object' &&
    window.Sentry
  ) {
    const Sentry = window.Sentry as {
      captureException: (error: unknown, options?: unknown) => void
    }
    if (context) {
      Sentry.captureException(error, {
        contexts: {
          custom: context,
        },
      })
    } else {
      Sentry.captureException(error)
    }
  }
}

/**
 * 安全にエラーをログ出力
 * @param prefix - ログのプレフィックス
 * @param error - エラーオブジェクト
 */
export const logError = (prefix: string, error: unknown): void => {
  // エラーログ機能（コンソール出力は削除、Sentryへの送信は維持）
  captureException(error, { context: prefix })
}

type ToastFunction = (
  message: string,
  type: 'success' | 'error' | 'warning' | 'info',
  duration?: number
) => void

/**
 * 非同期処理のエラーハンドリング
 * コンポーネントのアンマウント状態を考慮してSentry送信+Toast表示
 */
export const handleAsyncError = (
  error: unknown,
  userMessage: string,
  isMounted: React.MutableRefObject<boolean>,
  showToast?: ToastFunction
): void => {
  if (!isMounted.current) {
    return
  }

  captureException(error)

  if (showToast) {
    showToast(userMessage, 'error', 3000)
  }
}
